import { FC, useEffect, useMemo, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentDetails } from './Form/PaymentDetails';
import theme from 'config/theme';
import {
  NavigationAmountDetails,
  NavigationContainer,
  OlNavigation,
  ProgressBar,
} from 'screens/Checkout/styled';
import {
  formatNumberAsCurrency,
  getPathWithRef,
  noop,
  isObjectEmpty,
  getUrlParams,
  getUrlSearchParamsStrNoMethod,
} from 'helpers/utils';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { IUserData } from 'store/user/types';
import {
  canSkipPersonalDetails,
  getDisplayTextFromRecurringInterval,
  getCheckoutInitialValues,
  getIsEmployerAddressRequiredByCandidates,
  getOfflineSubmitButtonText,
  getPaymentMethodFromStripe,
  getPayRequestBodyStripe,
  getProgressBarWidthStripe,
  getUpdatedUser,
  InitialValuesInterface,
  stripeRecurringIntervalCountFromOath,
  stripeRecurringIntervalFromOath,
} from 'screens/Checkout/Form/utils';
import { Link } from 'components/Link';
import { Wizard } from 'screens/Checkout/Form/Wizard';
import { Details } from 'screens/Checkout/Form/Details';
import { Review } from 'screens/Checkout/Form/Review';
import {
  postCreateSubscriptionStripeAPI,
  postPaymentIntentStripeAPI,
  postTransactionWire,
  storeStripePaymentIntent,
} from 'store/pay/actions';
import {
  clearCheckoutIdempotencyKey,
  getCheckoutIdempotencyKey,
  setCheckoutIdempotencyKey,
} from 'services/storage';
import { v4 as uuidv4 } from 'uuid';
import { updateUserProfile } from 'store/user/actions';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import {
  AUTOPILOT_CLIENT_ID,
  getActionNoun,
  getCheckoutConfirmationUrl,
  getIsTippingVariant2,
  getStandardConfirmationPath,
  MIN_DONATION_AUTOPILOT,
} from 'helpers/donate';
import { FORM_ERROR } from 'final-form';
import { OathRecurringInterval } from 'store/pay/types';
import { AxiosError } from 'axios';
import { TicketWithQuantitySelected } from 'store/eventPage/types';
import { formatTicketSummary, getTicketCount } from 'helpers/event';
import {
  trackCheckoutReviewViewed,
  trackCheckoutStep1Viewed,
  trackCompleteDonationClicked,
  trackEvent,
  PaymentProcessor,
} from 'helpers/mixpanelEvents';
import { datadogLogs } from '@datadog/browser-logs';
import { setExpressCheckout } from 'store/expressCheckout/slice';
import { getExpressCheckout } from 'selectors/expressCheckout';
import { getDonatePageData } from 'selectors/donatePage';
import { useStatsigClient } from '@statsig/react-bindings';
import { useDefaultTipAmount } from 'hooks/statsig';
import { StripeError, StripeErrorType } from '@stripe/stripe-js';

const STRIPE_USER_ERROR_TYPES: readonly StripeErrorType[] = ['card_error', 'validation_error'];
const STRIPE_ERROR_MSG_GENERIC = 'If the problem continues, please contact support.';

const getCheckoutLogData = ({
  isRecurring,
  idempotencyKey,
  clientId,
  pageId,
  pageQueryString,
  donationAmount,
  endpoint,
  selectedPaymentMethod,
}: {
  isRecurring: boolean;
  idempotencyKey?: string;
  clientId?: number;
  pageId?: number;
  pageQueryString?: string;
  donationAmount: number;
  endpoint?: string;
  selectedPaymentMethod?: string;
}) => ({
  component: 'Checkout',
  endpoint,
  is_recurring: isRecurring,
  idempotency_key: idempotencyKey,
  client_id: clientId,
  page_id: pageId,
  page_query_string: pageQueryString,
  donation_amount: donationAmount,
  selected_payment_method: selectedPaymentMethod,
});

const getStripeErrorData = ({
  stripeError,
  isRecurring,
  idempotencyKey,
  clientId,
  pageId,
  pageQueryString,
  donationAmount,
  selectedPaymentMethod,
}: {
  stripeError: StripeError;
  isRecurring: boolean;
  idempotencyKey: string;
  clientId?: number;
  pageId?: number;
  pageQueryString?: string;
  donationAmount: number;
  selectedPaymentMethod?: string;
}) => ({
  payment_intent_id: stripeError.payment_intent?.id,
  error_type: stripeError.type,
  error_code: stripeError.code,
  decline_code: stripeError.decline_code,
  param: stripeError.param,
  ...getCheckoutLogData({
    isRecurring,
    idempotencyKey,
    clientId,
    pageId,
    pageQueryString,
    donationAmount,
    endpoint: 'Stripe confirm payment intent',
    selectedPaymentMethod,
  }),
});

const handleStripeConfirmError = ({
  stripeError,
  isRecurring,
  idempotencyKey,
  clientId,
  pageId,
  pageQueryString,
  donationAmount,
  selectedPaymentMethod,
}: {
  stripeError: StripeError;
  isRecurring: boolean;
  idempotencyKey: string;
  clientId?: number;
  pageId?: number;
  pageQueryString?: string;
  donationAmount: number;
  selectedPaymentMethod?: string;
}) => {
  try {
    const isUserError = STRIPE_USER_ERROR_TYPES.includes(stripeError.type);
    const logMsg = 'Stripe confirmPayment failed';
    const logData = {
      ...getStripeErrorData({
        stripeError,
        isRecurring,
        idempotencyKey,
        clientId,
        pageId,
        pageQueryString,
        donationAmount,
        selectedPaymentMethod,
      }),
      errorMessage: stripeError.message,
    };
    try {
      if (isUserError) {
        datadogLogs.logger.warn(logMsg, logData, new Error(`${logMsg}: ${stripeError.type}`));
      } else {
        datadogLogs.logger.error(logMsg, logData, new Error(`${logMsg}: ${stripeError.type}`));
      }
    } catch {
      noop();
    }
    return isUserError && stripeError.message ? stripeError.message : STRIPE_ERROR_MSG_GENERIC;
  } catch {
    return STRIPE_ERROR_MSG_GENERIC;
  }
};

const getOnSubmitStripeErrorMsg = (error: any) => {
  let errorMessage = 'Error creating payment intent.';
  if (error instanceof AxiosError && error.response?.data?.detail?.length > 0) {
    try {
      errorMessage = '';
      const errorMsgPart1 = error.response?.data?.detail[0].loc?.join(', ');
      const errorMsgPart2 = error.response?.data?.detail[0].msg;
      if (errorMsgPart1) {
        errorMessage += errorMsgPart1 + ' ';
      }
      if (errorMsgPart2) {
        errorMessage += errorMsgPart2;
      }
      if (!errorMessage) {
        errorMessage = error.response?.data?.detail;
      }
    } catch {
      noop();
    }
  }
  return errorMessage;
};

interface Props {
  candidates: CandidateType[];
  clientId?: number;
  clientName?: string;
  donationAmount: number;
  hasStripeSession: boolean;
  isRecurring: boolean;
  pageId?: number;
  pageName?: string;
  pageOwner: string;
  pageQueryString?: string;
  recurringInterval: OathRecurringInterval;
  setDonationAmountWithTip: (amount: number) => void;
  ticketSelections: TicketWithQuantitySelected[];
  user?: IUserData;
}

export const CheckoutForm: FC<Props> = ({
  candidates,
  clientId,
  clientName,
  donationAmount,
  hasStripeSession,
  isRecurring,
  pageId,
  pageName,
  pageOwner,
  pageQueryString,
  recurringInterval,
  setDonationAmountWithTip,
  ticketSelections,
  user,
}) => {
  const { urlDonorSelectedMethod, urlDonorSelectedMethodType, urlPartner, urlExpressCheckout } =
    getUrlParams();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const expressCheckoutData = useAppSelector(getExpressCheckout);
  const page = useAppSelector(getDonatePageData);
  const actionNoun = getActionNoun(page);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [prevSubmittedValuesStr, setPrevSubmittedValuesStr] = useState('');
  const { logEvent: statsigLogEvent } = useStatsigClient();
  const defaultTipAmount = useDefaultTipAmount(
    Number(donationAmount),
    getIsTippingVariant2(clientId)
  );

  const isExpressCheckout = urlExpressCheckout === 'true';
  const isPledge = urlDonorSelectedMethod === 'pledge';
  // A pledge on this Stripe-hosted page never reaches Stripe — see `PaymentProcessor`.
  const paymentProcessor: PaymentProcessor = isPledge ? 'Offline' : 'Stripe';
  const formPages = [
    {
      navigationTitle: '2. Personal Details',
    },
    {
      navigationTitle: isPledge ? '3. Confirm' : '3. Payment & Confirm',
    },
  ];

  // recompute only when donationAmount & user change. isExpressCheckout and expressCheckoutData are determined upstream
  const initialValues = useMemo(() => {
    if (!donationAmount) return {};
    const userData = isExpressCheckout && !user ? expressCheckoutData : user;
    return getCheckoutInitialValues(defaultTipAmount, userData, isPledge);
  }, [donationAmount, user?.email, clientId, defaultTipAmount]);

  useEffect(() => {
    if (!getCheckoutIdempotencyKey()) {
      setCheckoutIdempotencyKey(uuidv4());
    }
  }, []);

  useEffect(() => {
    if (!isObjectEmpty(initialValues)) {
      setActivePageIndex(
        canSkipPersonalDetails(initialValues as InitialValuesInterface, candidates) ? 1 : 0
      );
    }
  }, [initialValues]);

  const onTipChange = (tip: number) => {
    setDonationAmountWithTip(donationAmount + tip);
    trackEvent('Checkout Tip Changed', {
      is_recurring: isRecurring,
      client_id: clientId,
      amount: donationAmount,
      tip_amount: tip,
      is_stripe: true,
      payment_processor: paymentProcessor,
      is_express: isExpressCheckout,
    });
  };

  const onPersonalDetailsComplete = (values: any) => {
    // update user with form values
    try {
      if (user) {
        const updatedUser = getUpdatedUser(user, values);
        if (!isObjectEmpty(updatedUser)) {
          dispatch(updateUserProfile(updatedUser));
        }
      }
    } catch {
      noop(); // allow for checkout if this fails
    }
    // store personal data for express checkout pre-fill (used by logged-out users)
    dispatch(
      setExpressCheckout({
        email: values.email,
        first_name: values.first_name,
        last_name: values.last_name,
        address1: values.address1,
        address2: values.address2,
        city: values.city,
        state: values.state,
        zipcode: values.zipcode,
        country: values.country,
        occupation: values.occupation,
        employer: values.employer,
        employer_address1: values.employer_address1,
        employer_address2: values.employer_address2,
        employer_city: values.employer_city,
        employer_state: values.employer_state,
        employer_zipcode: values.employer_zipcode,
        employer_country: values.employer_country,
      })
    );
  };

  const onNext = (currentPageIndex: number, values: any) => {
    if (currentPageIndex === 0) {
      onPersonalDetailsComplete(values);
      trackEvent('Checkout Step 1 Continue Button Clicked', {
        is_recurring: isRecurring,
        client_id: clientId,
        amount: donationAmount,
        is_stripe: true,
        payment_processor: paymentProcessor,
        is_express: isExpressCheckout,
      });
    }
  };

  const onDetailsView = () => {
    trackCheckoutStep1Viewed({
      is_recurring: isRecurring,
      client_id: clientId,
      amount: donationAmount,
      is_stripe: true,
      payment_processor: paymentProcessor,
      is_express: isExpressCheckout,
    });
  };

  const onReviewView = () => {
    trackCheckoutReviewViewed({
      is_recurring: isRecurring,
      client_id: clientId,
      amount: donationAmount,
      is_stripe: true,
      payment_processor: paymentProcessor,
      is_express: isExpressCheckout,
    });
  };

  const onSubmit = async formValues => {
    try {
      datadogLogs.logger.info(
        `Starting stripe payment`,
        getCheckoutLogData({
          isRecurring: false,
          clientId,
          pageId,
          pageQueryString,
          donationAmount,
        })
      );
    } catch {
      noop();
    }
    trackCompleteDonationClicked({
      page_owner: pageOwner,
      is_recurring: false,
      client_id: clientId,
      amount: donationAmount,
      is_stripe: true,
      payment_processor: 'Stripe',
      is_express: isExpressCheckout,
      has_tip_option: true,
      tip_amount: formValues.tip ?? 0,
    });
    if (formValues.tip) {
      statsigLogEvent('tip_submitted', formValues.tip);
    }

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    // Trigger form validation and wallet collection
    // We can get selectedPaymentMethod here and add to the request body for cc vs ach etc
    const { error: submitError, selectedPaymentMethod } = await elements.submit();
    if (submitError) {
      try {
        datadogLogs.logger.warn(
          `Error creating stripe payment`,
          getCheckoutLogData({
            isRecurring: false,
            clientId,
            pageId,
            pageQueryString,
            donationAmount,
            endpoint: 'Stripe elements.submit',
            selectedPaymentMethod,
          })
        );
      } catch {
        noop();
      }
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: submitError.message,
        });
      });
    }

    const paymentInstrument = getPaymentMethodFromStripe(selectedPaymentMethod);

    const requestBody = getPayRequestBodyStripe({
      formValues,
      candidates,
      clientId,
      clientName,
      pageId,
      pageName,
      pageQueryString,
      selectedPaymentMethod,
      ticketSelections,
    });
    const requestBodyStr = JSON.stringify(requestBody);

    // if there is no idempotency key or the data changed from the last request, create one
    if (
      !getCheckoutIdempotencyKey() ||
      (prevSubmittedValuesStr && requestBodyStr !== prevSubmittedValuesStr)
    ) {
      setCheckoutIdempotencyKey(uuidv4());
    }

    const idempotencyKey = getCheckoutIdempotencyKey();
    if (!idempotencyKey) {
      try {
        datadogLogs.logger.warn(
          `Error creating stripe payment`,
          getCheckoutLogData({
            isRecurring: false,
            clientId,
            pageId,
            pageQueryString,
            donationAmount,
            endpoint: 'No idempotency key found',
            selectedPaymentMethod,
          })
        );
      } catch {
        noop();
      }
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: 'No idempotency key found. Cannot complete payment.',
        });
      });
    }

    try {
      datadogLogs.logger.info(
        `Creating stripe payment intent.`,
        getCheckoutLogData({
          isRecurring: false,
          clientId,
          pageId,
          pageQueryString,
          donationAmount,
          idempotencyKey,
          selectedPaymentMethod,
        })
      );
    } catch {
      noop();
    }

    try {
      // Create the PaymentIntent and obtain clientSecret
      const response = await postPaymentIntentStripeAPI(
        requestBody,
        hasStripeSession,
        idempotencyKey
      );
      const { client_secret: clientSecret } = response.data;
      setPrevSubmittedValuesStr(requestBodyStr);

      try {
        datadogLogs.logger.info(
          `Confirming stripe payment intent.`,
          getCheckoutLogData({
            isRecurring: false,
            clientId,
            pageId,
            pageQueryString,
            donationAmount,
            idempotencyKey,
            selectedPaymentMethod,
          })
        );
      } catch {
        noop();
      }

      // Confirm the PaymentIntent using the details collected by the Payment Element
      const stripeResponse = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: getCheckoutConfirmationUrl(pageQueryString, paymentInstrument),
        },
        redirect: 'if_required',
      });

      if (stripeResponse.error) {
        const errorMsg = handleStripeConfirmError({
          stripeError: stripeResponse.error,
          isRecurring: false,
          idempotencyKey,
          clientId,
          pageId,
          pageQueryString,
          donationAmount,
          selectedPaymentMethod,
        });
        clearCheckoutIdempotencyKey();
        return new Promise(resolve => {
          resolve({
            [FORM_ERROR]: errorMsg,
          });
        });
      }
      if (stripeResponse.paymentIntent) {
        try {
          datadogLogs.logger.info(
            `Successfully confirmed stripe payment intent. Checkout complete.`,
            getCheckoutLogData({
              isRecurring: false,
              clientId,
              pageId,
              pageQueryString,
              donationAmount,
              idempotencyKey,
              selectedPaymentMethod,
            })
          );
        } catch {
          noop();
        }
        statsigLogEvent('checkout_completed', donationAmount);
        dispatch(storeStripePaymentIntent(stripeResponse.paymentIntent));
        navigate(getStandardConfirmationPath(pageQueryString, paymentInstrument));
      }
    } catch (error) {
      const errorMessage = getOnSubmitStripeErrorMsg(error);
      try {
        datadogLogs.logger.warn(
          `Error creating stripe payment: ${errorMessage}`,
          getCheckoutLogData({
            isRecurring: false,
            idempotencyKey,
            clientId,
            pageId,
            pageQueryString,
            donationAmount,
            selectedPaymentMethod,
          })
        );
      } catch {
        noop();
      }
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: errorMessage,
        });
      });
    }
  };

  const onRecurringSubmit = async formValues => {
    try {
      datadogLogs.logger.info(
        `Starting stripe payment`,
        getCheckoutLogData({
          isRecurring: true,
          clientId,
          pageId,
          pageQueryString,
          donationAmount,
        })
      );
    } catch {
      noop();
    }
    trackCompleteDonationClicked({
      page_owner: pageOwner,
      is_recurring: true,
      client_id: clientId,
      amount: donationAmount,
      is_stripe: true,
      payment_processor: paymentProcessor,
      is_express: isExpressCheckout,
      has_tip_option: true,
      tip_amount: formValues.tip ?? 0,
    });
    if (formValues.tip) {
      statsigLogEvent('tip_submitted', formValues.tip);
    }
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    if (clientId === AUTOPILOT_CLIENT_ID && donationAmount < MIN_DONATION_AUTOPILOT) {
      try {
        datadogLogs.logger.info(
          `User attempted to donate less than ${MIN_DONATION_AUTOPILOT} on autopilot`,
          getCheckoutLogData({
            isRecurring: true,
            clientId,
            pageId,
            pageQueryString,
            donationAmount,
          })
        );
      } catch {
        noop();
      }
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: `Oath on Autopilot donations must be at least ${formatNumberAsCurrency(
            MIN_DONATION_AUTOPILOT
          )}.`,
        });
      });
    }

    // Trigger form validation and wallet collection
    // We can get selectedPaymentMethod here and add to the request body for cc vs ach etc
    const { error: submitError, selectedPaymentMethod } = await elements.submit();
    if (submitError) {
      try {
        datadogLogs.logger.warn(
          `Error creating stripe payment`,
          getCheckoutLogData({
            isRecurring: true,
            clientId,
            pageId,
            pageQueryString,
            donationAmount,
            endpoint: 'Stripe elements.submit',
            selectedPaymentMethod,
          })
        );
      } catch {
        noop();
      }
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: submitError.message,
        });
      });
    }

    const paymentInstrument = getPaymentMethodFromStripe(selectedPaymentMethod);

    const requestBody = getPayRequestBodyStripe({
      formValues,
      candidates,
      clientId,
      clientName,
      pageId,
      pageName,
      pageQueryString,
      selectedPaymentMethod,
    });
    requestBody.subscription_interval = stripeRecurringIntervalFromOath(recurringInterval);
    const recurringIntervalCount = stripeRecurringIntervalCountFromOath(recurringInterval);
    if (recurringIntervalCount > 1) {
      requestBody.subscription_interval_count = recurringIntervalCount;
    }
    const requestBodyStr = JSON.stringify(requestBody);

    // if there is no idempotency key or the data changed from the last request, create one
    if (
      !getCheckoutIdempotencyKey() ||
      (prevSubmittedValuesStr && requestBodyStr !== prevSubmittedValuesStr)
    ) {
      setCheckoutIdempotencyKey(uuidv4());
    }

    const idempotencyKey = getCheckoutIdempotencyKey();
    if (!idempotencyKey) {
      try {
        datadogLogs.logger.warn(
          `Error creating stripe payment`,
          getCheckoutLogData({
            isRecurring: true,
            clientId,
            pageId,
            pageQueryString,
            donationAmount,
            endpoint: 'No idempotency key found',
            selectedPaymentMethod,
          })
        );
      } catch {
        noop();
      }
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: 'No idempotency key found. Cannot complete payment.',
        });
      });
    }

    try {
      datadogLogs.logger.info(
        `Creating stripe payment intent.`,
        getCheckoutLogData({
          isRecurring: true,
          clientId,
          pageId,
          pageQueryString,
          donationAmount,
          idempotencyKey,
          selectedPaymentMethod,
        })
      );
    } catch {
      noop();
    }

    try {
      // Create the Subscription and obtain clientSecret
      const response = await postCreateSubscriptionStripeAPI(
        requestBody,
        hasStripeSession,
        idempotencyKey
      );
      const { client_secret: clientSecret } = response.data;
      setPrevSubmittedValuesStr(requestBodyStr);

      try {
        datadogLogs.logger.info(
          `Confirming stripe payment intent.`,
          getCheckoutLogData({
            isRecurring: true,
            clientId,
            pageId,
            pageQueryString,
            donationAmount,
            idempotencyKey,
            selectedPaymentMethod,
          })
        );
      } catch {
        noop();
      }

      // Confirm the PaymentIntent using the details collected by the Payment Element
      const stripeResponse = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: getCheckoutConfirmationUrl(pageQueryString, paymentInstrument),
        },
        redirect: 'if_required',
      });

      if (stripeResponse.error) {
        const errorMsg = handleStripeConfirmError({
          stripeError: stripeResponse.error,
          isRecurring: true,
          idempotencyKey,
          clientId,
          pageId,
          pageQueryString,
          donationAmount,
          selectedPaymentMethod,
        });
        clearCheckoutIdempotencyKey();
        return new Promise(resolve => {
          resolve({
            [FORM_ERROR]: errorMsg,
          });
        });
      }
      if (stripeResponse.paymentIntent) {
        try {
          datadogLogs.logger.info(
            `Successfully confirmed stripe payment intent. Checkout complete.`,
            getCheckoutLogData({
              isRecurring: true,
              clientId,
              pageId,
              pageQueryString,
              donationAmount,
              idempotencyKey,
              selectedPaymentMethod,
            })
          );
        } catch {
          noop();
        }
        statsigLogEvent('checkout_completed', donationAmount);
        dispatch(storeStripePaymentIntent(stripeResponse.paymentIntent));
        navigate(getPathWithRef(getStandardConfirmationPath(pageQueryString, paymentInstrument)));
      }
    } catch (error) {
      const errorMessage = getOnSubmitStripeErrorMsg(error);
      try {
        datadogLogs.logger.warn(
          `Error creating recurring payment: ${errorMessage}`,
          getCheckoutLogData({
            isRecurring: true,
            idempotencyKey,
            clientId,
            pageId,
            pageQueryString,
            donationAmount,
            selectedPaymentMethod,
          })
        );
      } catch {
        noop();
      }
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: errorMessage,
        });
      });
    }
  };

  const onWireSubmit = async formValues => {
    trackCompleteDonationClicked({
      page_owner: pageOwner,
      payment_method: urlDonorSelectedMethodType,
      is_recurring: false,
      client_id: clientId,
      amount: donationAmount,
      is_stripe: true,
      payment_processor: paymentProcessor,
      is_express: isExpressCheckout,
      has_tip_option: false,
      tip_amount: 0,
    });
    const requestBody = getPayRequestBodyStripe({
      formValues,
      candidates,
      clientName,
      clientId,
      pageId,
      pageName,
      pageQueryString,
      selectedPaymentMethod: 'pledge',
      selectedPaymentMethodType: urlDonorSelectedMethodType,
    });
    const requestBodyStr = JSON.stringify(requestBody);

    // if there is no idempotency key or the data changed from the last request, create one
    if (
      !getCheckoutIdempotencyKey() ||
      (prevSubmittedValuesStr && requestBodyStr !== prevSubmittedValuesStr)
    ) {
      setCheckoutIdempotencyKey(uuidv4());
    }

    const idempotencyKey = getCheckoutIdempotencyKey();
    if (!idempotencyKey) {
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: 'No idempotency key found. Cannot complete payment.',
        });
      });
    }

    try {
      await dispatch(postTransactionWire(requestBody, idempotencyKey, !!user));
      setPrevSubmittedValuesStr(requestBodyStr);
      navigate(
        `/donate/confirmation/pledge/${urlDonorSelectedMethodType}?${getUrlSearchParamsStrNoMethod()}`
      );
      return;
    } catch {
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: 'Error creating payment intent.',
        });
      });
    }
  };

  const backToDonatePath = getPathWithRef(`/donate?${getUrlSearchParamsStrNoMethod()}`);

  return (
    <>
      <div className="row">
        <NavigationContainer $justifyContent="space-between">
          <OlNavigation>
            <li>
              <Link to={backToDonatePath}>
                <span className="body-small-text semi-bold color-inkBlue">1. Amount</span>
                <NavigationAmountDetails $fontType="semiBold" $color={theme.colors.inkBlue}>
                  {' ('}
                  {formatNumberAsCurrency(donationAmount)}
                  {ticketSelections.length > 0 &&
                    ` - ${getTicketCount(ticketSelections)} ticket${
                      getTicketCount(ticketSelections) === 1 ? '' : 's'
                    }`}
                  {')'}
                </NavigationAmountDetails>
              </Link>
            </li>
            {formPages.map(({ navigationTitle }, index) => (
              <li key={index}>
                <button
                  onClick={() => index < activePageIndex && setActivePageIndex(index)}
                  disabled={index >= activePageIndex}
                >
                  <span
                    className={classNames('body-small-text', {
                      'semi-bold': index <= activePageIndex,
                      'color-black shadow-7': index > activePageIndex,
                    })}
                  >
                    {navigationTitle}
                  </span>
                </button>
              </li>
            ))}
          </OlNavigation>
          <ProgressBar $width={getProgressBarWidthStripe(activePageIndex)} />
        </NavigationContainer>
      </div>
      <div>
        {isRecurring ? (
          <span className="body-text color-black">
            Start your {getDisplayTextFromRecurringInterval(recurringInterval)}{' '}
          </span>
        ) : (
          <span className="body-text color-black">Complete your </span>
        )}
        {activePageIndex === 2 ? (
          <span className="body-text color-black">contribution:</span>
        ) : (
          <span className="body-text color-black semi-bold">
            {formatNumberAsCurrency(donationAmount)} contribution:
          </span>
        )}
      </div>
      {ticketSelections.length > 0 && (
        <span className="body-small-text color-gray">
          {formatTicketSummary(ticketSelections, donationAmount)}
        </span>
      )}
      <Wizard
        actionNoun={actionNoun}
        initialValues={initialValues}
        backToDonatePath={backToDonatePath}
        completeButtonText={
          isPledge
            ? getOfflineSubmitButtonText(urlDonorSelectedMethodType)
            : urlPartner === 'f4d-nvi'
              ? 'Complete contribution'
              : isRecurring
                ? `Complete ${getDisplayTextFromRecurringInterval(recurringInterval)} ${actionNoun}`
                : undefined
        }
        onBackToDonatePage={noop}
        onNext={onNext}
        onSubmit={isPledge ? onWireSubmit : isRecurring ? onRecurringSubmit : onSubmit}
        activePageIndex={activePageIndex}
        setActivePageIndex={setActivePageIndex}
        isSkipToReviewEnabled={false}
        onNavigateToLastPage={noop}
        validateArray={[() => ({}), () => ({}), () => ({})]}
      >
        <Details
          candidates={candidates}
          isEmployerAddressRequired={getIsEmployerAddressRequiredByCandidates(candidates)}
          isRecurring={isRecurring}
          onDetailsView={onDetailsView}
          showSecuredByStripe
        />
        <Review
          donationAmount={donationAmount}
          isTippingVariant2={getIsTippingVariant2(clientId)}
          numCandidates={candidates.length}
          backToDonatePath={backToDonatePath}
          onBackToDonatePage={noop}
          onTipChange={onTipChange}
          setActivePageIndex={setActivePageIndex}
          isCondensed={!isPledge}
          isRecurring={isRecurring}
          recurringInterval={recurringInterval}
          singleLine={!isPledge}
          isPledge={isPledge}
          pledgeType={urlDonorSelectedMethodType}
          hideTip={isPledge}
          onView={onReviewView}
        >
          {!isPledge && <PaymentDetails />}
        </Review>
      </Wizard>
    </>
  );
};
